import React, {
	useRef,
	useContext,
	useEffect,
	useState,
	useCallback,
	useLayoutEffect,
} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import Button, { ILink } from 'tbk-components/src/components/Button';
import Modal from 'tbk-components/src/components/Modal';
import Icon from 'tbk-components/src/components/Icon';
import ResourcesFilterBar from '../ResourcesFilterBar';
import withAppContext from 'tbk-components/src/hooks/withAppContext';
import Dropdown from 'bootstrap/js/dist/dropdown';
import { debounce, has, set } from 'lodash';
import SmallProductGrid, {
	ISmallProduct,
	ISmallProductsI18n,
} from '../ResourceIndex/SmallProductGrid';
import { useTranslation } from 'react-i18next';
import Loading from 'tbk-components/src/components/Loading';
import qs from 'qs';

export interface ISearchModalI18nProps {
	noResultsFound?: string;
	advancedSearch?: string;
	youAreViewing?: string;
	resultsOf?: string;
	viewAll?: string;
	searchPlaceholder?: string;
	threeCharacterMinimum?: string;
}

export interface ISimpleSearchResult {
	header?: string;
	link?: ILink;
}

//This is the expected object returned for search results
export interface ISimpleSearchResults {
	simpleSearchList?: ISimpleSearchResult[];
	products?: ISmallProduct[];
	simpleSearchListTotal?: number;
	productsTotal?: number;
}
export interface ISearchModalProps extends IBaseElementProps {
	title?: string;
	/**
	 * @control richtext
	 */
	excerpt?: string;
	/**
	 * @noUI
	 */
	context?: any;
	/**
	 * @noUI
	 */
	advancedSearchUrl?: ILink;
	/**
	 * @noUI
	 */
	productSearchUrl?: ILink;
	/**
	 * @noUI
	 */
	assets?: any;
	fetchArgs?: any;
	searchResults?: any;
	enableSideProductListing?: boolean;
	sideProductsI18n?: ISmallProductsI18n;
	i18n?: ISearchModalI18nProps;
	onCancel?: () => void;
}

const SearchModal: React.FC<ISearchModalProps> = withAppContext(
	({
		className,
		classNames = [],
		context,
		fetchArgs,
		advancedSearchUrl,
		productSearchUrl,
		enableSideProductListing = true,
		i18n,
		sideProductsI18n,
		onCancel = () => void 0,
	}) => {
		const { t } = useTranslation();
		// @ts-ignore
		const { assets: contextAssets, Api } = useContext(context);
		const [assets, setAssets] = useState<any>(null);
		const [termMap, setTermMap] = useState<any>(null);

		const [searchResults, setSearchResults] =
			useState<ISimpleSearchResults>();
		const topRef = useRef(null);
		const [loading, setLoading] = useState<boolean>(false);
		const [isSearching, setIsSearching] = useState(false);
		const [sideProducts, setSideProductListing] = useState<any>(null);
		const [productTotal, setProductTotal] = useState<number>(0);
		const [activeTerms, setActiveTerms] = useState<any>([]);
		const taxonomy_parameters = ((taxonomies) => {
			const t: any = {};
			(taxonomies || []).forEach((tax: any) => {
				t[tax.taxonomy] = 'csv';
			});
			return t;
		})(assets?.taxonomies);

		const [filters, setFilters] = useState({
			page: 'int',
			...taxonomy_parameters,
			search: 'string',
			total: 'int',
		});

		const [advancedUrlWithQuery, setAdvancedUrlWithQuery] =
			useState<string>(advancedSearchUrl?.href || '');

		const { total: ignored, ...relevantArgs } = filters;

		const fetchPostsFromApi = useCallback(
			debounce((args: any) => {
				//if args.search has a value, update the advancedUrl to include the search value
				if (args.search) {
					setAdvancedUrlWithQuery(
						`${advancedSearchUrl?.href}#!search=${args.search}`,
					);
				}
				const getBlogRoll = Api['getBlogRollSearchWP'];
				const modifiedArgs = { ...args, isSimpleSearch: true };
				getBlogRoll(modifiedArgs)
					.then((results: any) => {
						setSearchResults(results);
					})
					.finally(() => {
						setLoading(false);
					});

				if (enableSideProductListing) {
					Api.getSiteSearchWP({
						posts_per_page: 6,
						post_type: [
							'gnss_product',
							'cable_access',
							'space_satcom',
							'space_antenna',
						],
						isSimpleSearch: true,
						allSearchList: false,
						search: args.search,
					}).then((results: any) => {
						if (results.products)
							setSideProductListing(results.products);

						if (results.productsTotal)
							setProductTotal(results.productsTotal);
					});
				}
			}, 500),
			['getBlogRollSearchWP'],
		);

		useEffect(() => {
			const assetReqArgs = { ...fetchArgs, isSimpleSearch: true };
			const setAssetsAndTermMap = (results: any) => {
				setAssets(results);
				const map: any = {};
				if (results.taxonomies) {
					results.taxonomies.forEach((taxonomy: any) => {
						taxonomy.terms.forEach((term: any) => {
							map[term.term_id] = term;
						});
					});
				}
				setTermMap(map);
			};
			if (contextAssets) {
				setAssetsAndTermMap(contextAssets);
			} else {
				Api.getBlogRollAssets(assetReqArgs).then((results: any) => {
					setAssetsAndTermMap(results);
				});
			}

			if (!topRef.current) {
				return;
			}

			const initDropdown = () => {
				const dropdownTriggerList = [].slice.call(
					topRef.current.querySelectorAll('.dropdown'),
				);

				dropdownTriggerList.map(
					(dropdownTriggerEl) => new Dropdown(dropdownTriggerEl),
				);
			};

			initDropdown();
		}, [contextAssets, Api, fetchArgs]);

		//When Modal is opened/loaded, focus on the search input '.search__input'
		useLayoutEffect(() => {
			if (!topRef.current) {
				return;
			}
			const searchInput = topRef.current.querySelector('.search__input');
			if (searchInput) {
				searchInput.focus();
			}
		}, [topRef.current]);

		const onSearchChange = (e) => {
			setFilters({
				page: 1,
				...filters,
				search: e.target.value,
			});
			setSearchResults(null);
		};

		const onCheckFilter = (e) => {
			e.preventDefault();
			const input = e?.target;
			const taxonomy = input.name; //Store this name as the object name //post_type
			const value = input.value; // blog
			//Check if Taxonomy already exists in the filters object
			let filterTaxonomies = filters[taxonomy] || [];
			const isAll = input.value.match(/(^|:)all$/);
			//If it does not exist, add it to the object
			if (!has(filters, taxonomy)) {
				set(filters, taxonomy, []);
			}
			//If the input is checked, add the value to the array
			if (input.checked) {
				if (filterTaxonomies.indexOf(value) === -1) {
					filterTaxonomies.push(value);
				}
			} else {
				//If the input is not checked, remove the value from the array
				const index = filterTaxonomies.indexOf(value);
				if (index !== -1) {
					filterTaxonomies.splice(index, 1);
				}
			}

			if (isAll) {
				filterTaxonomies = [];
				input.checked = true;
			}
			//Update the state
			setFilters({
				...filters,
				[taxonomy]: filterTaxonomies,
				page: 1,
			});
			setSearchResults(null);
		};
		const searchArgs = qs.stringify(relevantArgs);
		useEffect(() => {
			if (!assets) {
				return;
			}
			let args = qs.parse(searchArgs.replace(/^#!/, ''), {
				ignoreQueryPrefix: true,
			});
			args.posts_per_page = assets?.posts_per_page;
			if (fetchArgs?.posts_per_page) {
				args.posts_per_page = fetchArgs.posts_per_page;
			}
			args.simpleSearch = 'true';
			if (fetchArgs) {
				args = {
					...fetchArgs,
					...args,
				};
			}

			fetchPostsFromApi(args);
			setSearchResults(null);
			setLoading(true);
			setIsSearching(true);
		}, [searchArgs]);

		//When filters changes or load, console log it
		useEffect(() => {
			if (!filters) return;
			//When filters changes, pop out `page`,  `search`, and `total` and set the rest in a variable
			const { page, search, total, ...rest } = filters;
			const activeTermsArray = Object.keys(rest).reduce((acc, key) => {
				return acc.concat(
					rest[key].map((term) => {
						return {
							taxonomy: key,
							term,
						};
					}),
				);
			}, []);

			setActiveTerms(activeTermsArray);
		}, [filters]);

		return (
			<Modal
				onCancel={onCancel}
				trapFocus
				className={cn(
					className || ['calian-search-modal', ...classNames],
					!loading && isSearching && 'has-content',
					isSearching && 'searching',
				)}
				headerClassNames={[
					'calian-search-modal__header border-0 d-inline-block position-absolute z-1 end-0',
				]}
				modalClassNames={['modal-dialog-search']}
				bodyClassNames={[
					'modal-body p-0 bg-transparent d-flex flex-column',
				]}
			>
				<div
					ref={topRef}
					className="modal-search-filter-bar p-4 pt-7 p-md-5 pt-md-8"
				>
					<ResourcesFilterBar
						searchPlaceholder={
							i18n?.searchPlaceholder
								? i18n.searchPlaceholder
								: t('Search all Calian websites')
						}
						searchTaxonomies={assets}
						filters={filters}
						assets={assets}
						onCheckFilter={onCheckFilter}
						onSearchChange={onSearchChange}
						listStyle={'default'}
						showFilterBar
						filterHeaderBackgroundStyle={'light'}
						showSearchResultsCount={false}
						goToAdvancedSearch
						searchAction={advancedSearchUrl?.href}
						onCancel={onCancel}
					/>
				</div>
				{isSearching && (
					<div className="search-modal-results-render pt-4 pt-lg-6 d-flex flex-column flex-grow-1">
						{loading ? (
							<div className="calian-search-modal__results-loading py-md-8 py-4">
								<Loading />
							</div>
						) : (
							<div
								className={cn(
									'modal-results-content py-4 py-md-5 px-3 pt-md-0 pt-0',
									searchResults?.products?.length > 0 &&
										'border-top',
								)}
							>
								{searchResults?.simpleSearchListTotal > 0 ||
								sideProducts?.length > 0 ? (
									<div>
										{sideProducts && (
											<SmallProductGrid
												products={sideProducts}
												productSearchUrl={
													productSearchUrl
												}
												i18n={sideProductsI18n}
												total={productTotal}
												desktopStyle="grid"
											/>
										)}
										{searchResults?.simpleSearchList
											?.length > 0 && (
											<ul className="results-listing list-unstyled">
												{searchResults.simpleSearchList.map(
													(result, index) => {
														return (
															<li
																key={index}
																className="result-item"
															>
																{result?.link
																	?.href && (
																	<a
																		href={
																			result
																				?.link
																				?.href
																		}
																		className={cn(
																			'border-top',
																			index ===
																				searchResults
																					.simpleSearchList
																					.length -
																					1 &&
																				'border-bottom',
																			'text-decoration-none text-charcoal px-3 py-2 d-flex justify-content-between gap-3',
																		)}
																	>
																		{result?.header && (
																			<div
																				className="mb-0"
																				dangerouslySetInnerHTML={{
																					__html: result?.header,
																				}}
																			/>
																		)}
																		<Icon name="arrow-right" />
																	</a>
																)}
															</li>
														);
													},
												)}
											</ul>
										)}
									</div>
								) : (
									<div className="no-results py-5 text-center">
										<p className="py-4">
											{filters.search.length < 3
												? i18n?.threeCharacterMinimum ||
												  t(
														'Please enter at least 3 characters',
												  )
												: i18n?.noResultsFound ||
												  t('No results found')}
										</p>
									</div>
								)}
							</div>
						)}
						{activeTerms && (
							<ul className="active-terms list-unstyled d-flex justify-content-start flex-wrap gap-2 mb-3">
								{/* List all the activeTerms as buttons to remove the filter on click */}
								{activeTerms.map((term, index) => {
									const termData = termMap[term.term];
									return (
										<li
											key={index}
											className="active-term-item"
										>
											<Button
												className="text-decoration-none d-flex bg-transparent flex-row-reverse gap-2 align-items-center rounded-5 overflow-hidden text-charcoal"
												label={termData?.name}
												icon="close"
												onClick={() => {
													const filterTaxonomies =
														filters[term.taxonomy];
													const index =
														filterTaxonomies.indexOf(
															term.term,
														);
													if (index !== -1) {
														filterTaxonomies.splice(
															index,
															1,
														);
													}
													setFilters({
														...filters,
														[term.taxonomy]:
															filterTaxonomies,
														page: 1,
													});
												}}
											/>
										</li>
									);
								})}
							</ul>
						)}
						<div className="advanced-search-cta border-top w-100 p-3">
							<div className="advanced-search-cta__container px-md-3 mx-auto w-100 p-0 d-flex gap-2 gap-md-4  flex-wrap justify-content-between">
								{advancedSearchUrl && (
									<Button
										className="text-underline mb-0"
										label={
											i18n?.advancedSearch ||
											'Advanced Search'
										}
										href={advancedUrlWithQuery}
									/>
								)}
								{searchResults?.simpleSearchListTotal > 0 && (
									<div className="total-result-counter d-flex flex-wrap gap-4">
										<p className="mb-0">
											{`${
												i18n?.youAreViewing ||
												'You are viewing'
											} ${
												searchResults.simpleSearchList
													.length
											}
											${i18n?.resultsOf || 'results of'} ${searchResults.simpleSearchListTotal}`}
										</p>

										{advancedSearchUrl && (
											<Button
												className="text-underline d-none d-md-flex mb-0"
												href={advancedUrlWithQuery}
												label={
													i18n?.viewAll || 'View All'
												}
												onClick={() => onCancel()}
											/>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				)}
			</Modal>
		);
	},
);
export default SearchModal;
